/**
 * Owner: Masa Bayu
 * File: qx-idb.ts
 * Date:Time: 2026-10-16 01:24
 * Description: IndexedDB helpers · handles · recent · draft session.
 */

const DB_NAME = 'QXWriterDB';
const DB_VERSION = 3;

export const STORE_HANDLES = 'fileHandles';
export const STORE_RECENT = 'recentFiles';
/** Full draft session tip + preview data URLs (survives sessionStorage quota). */
export const STORE_DRAFT_SESSION = 'draftSession';
/** Fasa 3 — artifak QXP yang diterbitkan pada peranti (bundle + status hub). */
export const STORE_QXP_ARTIFACTS = 'qxpArtifacts';

export function isQXWriterIdbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

/** Reuse one open · faster flush before reload. */
let dbPromise: Promise<IDBDatabase> | null = null;

export function openQXWriterDB(): Promise<IDBDatabase> {
  if (!isQXWriterIdbAvailable()) {
    return Promise.reject(new Error('IndexedDB is not available in this environment.'));
  }
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => {
        dbPromise = null;
        reject(request.error ?? new Error('IndexedDB open failed'));
      };
      request.onsuccess = () => resolve(request.result);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_HANDLES)) {
          db.createObjectStore(STORE_HANDLES);
        }
        if (!db.objectStoreNames.contains(STORE_RECENT)) {
          db.createObjectStore(STORE_RECENT, { keyPath: 'name' });
        }
        if (!db.objectStoreNames.contains(STORE_DRAFT_SESSION)) {
          db.createObjectStore(STORE_DRAFT_SESSION);
        }
        if (!db.objectStoreNames.contains(STORE_QXP_ARTIFACTS)) {
          db.createObjectStore(STORE_QXP_ARTIFACTS, { keyPath: 'publicationId' });
        }
      };
    });
  }
  return dbPromise;
}

export async function idbGet<T>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
  const db = await openQXWriterDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB get failed'));
  });
}

export async function idbPut<T>(storeName: string, value: T, key?: IDBValidKey): Promise<void> {
  const db = await openQXWriterDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    if (key === undefined) {
      store.put(value);
    } else {
      store.put(value, key);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB put failed'));
  });
}

export async function idbDelete(storeName: string, key: IDBValidKey): Promise<void> {
  const db = await openQXWriterDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    tx.objectStore(storeName).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB delete failed'));
  });
}

export async function idbGetAll<T>(storeName: string): Promise<T[]> {
  const db = await openQXWriterDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve((req.result as T[]) ?? []);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB getAll failed'));
  });
}
